<template>
    <div class="container">
        <v-title :lineage="lineage">
            {{isFormUpdating ? 'Update' : 'Add'}} Notification
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'notifications'}"></v-cancel>
                <r-button :loading="isLoading" type="primary" @click="save">Save Notification</r-button>
            </div>
        </v-title>
        <v-error-messages
            :has-frontend-errors="hasTriggeredErrors"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.bind.name.required">Please provide a name for this notification.</li>
            <li class="list-item" v-if="!$v.bind.emailActions.required">This notification must send at least one email.</li>
            <template v-if="validationAggregator.aggregatedResult">
                <li class="list-item" v-for="error in validationAggregator.aggregatedResult">{{error.name}}</li>
            </template>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Notification Info</h2>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="Notification Name"
                                auto-focus
                                :help-text="nameHelpText"
                                :validate="$v.bind.name"
                                v-model="bind.name"/>
                        </div>
                    </div>
                </div>
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item">
                            <v-tag-input
                                label="Labels"
                                :help-text="labelHelpText"
                                :tags="existingLabels"
                                v-model="bind.labels"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Conditions</h2>
            <template slot="description">
                <p :class="{'text-muted': true, 'stack-s': isReminderEvent}">
                    Define conditions to filter when this notification should trigger.
                </p>
                <p class="text-muted" v-if="isReminderEvent">
                    Tip: set a condition on the {{reminderNumberProperty}} to overwrite a specific reminder.
                </p>
            </template>
            <div slot="content">
                <condition-builder
                    v-if="isDefinitionReady"
                    :criteria="bind.criteria"
                    :event="systemEvent"
                    @update="updateCriteria"
                    ruleType="notification"
                />
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Emails</h2>
            <div slot="description">
                <p class="text-muted stack-s">
                    Add email messages to be sent when the conditions for this notification are met. Email messages can be individually disabled.
                </p>
                <p class="text-muted"
                    v-if="$auth.$pages[rulesEngineRoute.name].isAllowed && isFormUpdating">
                    Want to manage actions other than sending emails?  <router-link :to="rulesEngineRoute">View this bind in the Rules Engine</router-link>
                </p>
            </div>
            <email-actions-list
                v-if="isDefinitionReady"
                :is-form-updating="isFormUpdating"
                :validation-aggregator="validationAggregator"
                :actions="bind.emailActions"
                :event="systemEvent"
                @add-action="addAction"
                @toggle-action-status="toggleActionStatus"
                @delete-action="deleteAction"
                @update-action="updateAction"
                :pre-processor="actionPreProcessor"
                :new-action-predicate="isNewlyAddedAction"
                slot="content"/>
        </v-form-tile>
        <div class="divider"></div>
        <v-error-messages
            :has-frontend-errors="hasTriggeredErrors"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.bind.name.required">Please provide a name for this notification.</li>
            <template v-if="validationAggregator.aggregatedResult">
                <li class="list-item" v-for="error in validationAggregator.aggregatedResult">{{error.name}}</li>
            </template>
        </v-error-messages>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'notifications'}"></v-cancel>
                <r-button :loading="isLoading" type="primary" @click="save">Save Notification</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import {required} from 'vuelidate/lib/validators';
    import EventModel from '@/common/models/rules/event-model';
    import RuleFactory from '@/common/models/rules/rule-factory';
    import vTitle from '@/components/v-title';
    import {
        vCancel,
        vCheckbox,
        vErrorMessages,
        vFormTile,
        vTagInput,
    } from '@/components/form';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import ValidationAggregator from '@/common/validation/validation-aggregator';
    import Base58 from '@/common/helpers/base58';
    import RuleSetModel from '@/common/models/rules/rule-set-model';
    import RuleModel from '@/common/models/rules/rule-model';
    import actionPreProcessor from '@/modules/rules-engine/components/rule-form/action-pre-processor';
    import ReminderActionModel from '@/common/models/rules/actions/reminder-action-model';
    import EventTypes from '@/common/api/definitions/event-types';

    export default {
        name: 'notification-form',
        mixins: [
            FormLoaderMixin,
        ],
        components: {
            vTitle,
            vFormTile,
            vErrorMessages,
            vCancel,
            vCheckbox,
            vTagInput,
            EmailActionsList: () => import(/* webpackChunkName: "notifications" */ '@/modules/notifications/components/email-actions-list'),
            ConditionBuilder: () => import(/* webpackChunkName: "notifications" */ '@/modules/rules-engine/components/condition-builder/condition-builder'),
        },
        props: {
            eventType: String,
            itemIndex: String,
        },
        computed: {
            predicate() {
                return this.itemIndex !== undefined;
            },
            /**
             * Get the name of the ancestor item in the breadcrumb lineage
             * @returns {*}
             */
            ancestorName() {
                return this.$filters.unKebabCase(this.eventType);
            },
            lineage() {
                return [
                    {
                        [`${this.ancestorName} Event`]: this.previousRoute,
                    },
                ];
            },
            nameHelpText() {
                return `Use a unique name to help differentiate between multiple notifications.`;
            },
            labelHelpText() {
                return `Labels can be used to categorize your notifications and filter them. Enter values and press enter to add them as a label.`;
            },
            previousRoute() {
                return {
                    name: `view-notifications`,
                    params: {
                        eventType: this.eventType,
                    },
                };
            },
            rulesEngineRoute() {
                const action = this.isFormUpdating ? 'edit' : 'add';
                return {
                    name: `${action}-system-bind`,
                    params: {
                        eventType: this.eventType,
                    },
                };
            },
            systemEvent() {
                return new EventModel({eventType: this.eventType});
            },
            ruleIndex() {
                return Base58.decode(this.itemIndex);
            },
            itemLabels: {
                get() {
                    return (this.bind.labels || []).join(', ');
                },
                set(value) {
                    this.bind.labels = value
                        .split(',')
                        .map(label => label.trim());
                },
            },
            existingLabels() {
                const labels = this.$store.getters['rulesEngine/labels'];
                if (labels.length) {
                    return labels.reduce((result, label) => {
                        result[label] = label;
                        return result;
                    }, {});
                }
                return {};
            },
            hasTriggeredErrors() {
                return this.$v.bind.$error || (!!(this.validationAggregator.aggregatedResult && this.validationAggregator.aggregatedResult.length));
            },
            isDataRequired() {
                // Fetches data even for new rule.
                return true;
            },
            isReminderEvent() {
                return ReminderActionModel.ReminderEvents.includes(this.eventType);
            },
            reminderNumberProperty() {
                switch (this.eventType) {
                    case EventTypes.subscriptionTrialEndReminder:
                        return 'Subscription\'s trial reminder number';
                    case EventTypes.subscriptionRenewalReminder:
                        return 'Subscription\'s renewal reminder number';
                    case EventTypes.invoicePastDueReminder:
                        return 'Invoice\'s due reminder number';
                    case EventTypes.paymentCardExpirationReminder:
                        return 'Payment Card\'s expiration reminder number';
                    default:
                        return '...';
                }
            },
        },
        data() {
            return {
                bind: RuleFactory.create(RuleModel.Types.bind.family),
                ruleSet: new RuleSetModel(),
                // validation aggregator for all sub-components (actions)
                validationAggregator: new ValidationAggregator(),
                actionPreProcessor,
                newActions: [],
                isDefinitionReady: false,
            };
        },
        validations() {
            return {
                bind: {
                    name: {required},
                    emailActions: {
                        required,
                    },
                },
            };
        },
        methods: {
            getDelegate(callback) {
                return this.$api(({events}) => callback(events));
            },
            async fetchData() {
                this.isDefinitionReady = false;
                const params = {eventType: this.eventType};
                const {fields} = await this.getDelegate(type => type.getRules(params));
                this.ruleSet = new RuleSetModel(fields);
                // parse labels again because they might not exist in memory
                await this.$store.dispatch('rulesEngine/parseLabels', fields.rules);
                this.bind = RuleFactory.create(
                    this.bind.family,
                    this.isFormUpdating ? fields[this.bind.family][this.ruleIndex] : {},
                );
                this.isDefinitionReady = true;
            },
            addAction(name) {
                const [action] = RuleModel.mapRuleActions([{name}]);
                this.newActions.push(action.$internalId);
                this.bind.actions.push(action);
            },
            toggleActionStatus(index) {
                this.bind.actions[index].status = !this.bind.actions[index].status;
            },
            deleteAction(index) {
                const [deletedAction] = this.bind.actions.splice(index, 1);
                const spliceIndex = this.newActions.findIndex(v => v === deletedAction.$internalId);
                this.newActions.splice(spliceIndex, 1);
            },
            updateAction({action, index}) {
                // allow changes to actions only
                // if the rule isn't already in the process of saving
                if (!this.isLoading) {
                    // TODO block UI interactions instead of having to use this condition
                    this.$set(this.bind.actions, index, action);
                }
            },
            updateCriteria(criteria) {
                this.bind.criteria = criteria;
            },
            isNewlyAddedAction({$internalId}) {
                return this.newActions.find(v => v === $internalId) !== undefined;
            },
            async save() {
                this.$v.$touch();
                // always trigger aggregated validation
                const actionErrors = this.validationAggregator.validateAggregated();
                if (!this.$v.bind.$error && !actionErrors) {
                    if (this.isFormUpdating) {
                        // when updating, reassign at current index within collection
                        this.ruleSet[this.bind.family][this.ruleIndex] = this.bind;
                    } else {
                        // otherwise push at the end of the collection
                        this.ruleSet[this.bind.family].push(this.bind);
                    }
                    await this.pushUpdate();
                }
            },
            async pushUpdate() {
                this.isLoading = true;
                try {
                    const filters = {
                        limit: 1,
                        eventType: this.eventType,
                    };
                    const {items} = await this.getDelegate(type => type.getRulesHistory(filters));
                    const [latest = false] = items;
                    // check if the latest event is of the same version
                    if (latest && latest.fields.version !== this.ruleSet.version) {
                        this.$toast.negative(
                            `This ${this.bind.type} has been modified since you started editing it. Your modification was discarded and the data was refreshed.`,
                            {delay: 10000},
                        );
                        // TODO cache changes in store and allow restore
                        await this.fetchData();
                        return;
                    }
                    // process any action steps before continuing
                    await this.actionPreProcessor.resolve();
                    const params = {
                        data: this.ruleSet.getPayload(),
                        eventType: this.eventType,
                    };
                    await this.getDelegate(type => type.updateRules(params));
                    this.$router.push(this.previousRoute);
                } catch (error) {
                    this.$toast.fromError(error, `Unable to modify this ${this.bind.type} at this time. Please try again later or contact support.`);
                    this.isLoading = false;
                }
            },
        },
    };
</script>
